/**
 * Xarita ma'lumot turlari va GeoJSON yordamchilari — dvigatelga (`MapView`)
 * bog'liq emas, shuning uchun sahifalar bularni yengil import qiladi.
 */

export interface MapPoint {
  id: string;
  lat: number;
  lon: number;
  color?: string;
  title: string;
  subtitle?: string;
  /** Popup'da "Batafsil" bo'limida ko'rsatiladigan xom atributlar (metadata). */
  properties?: Record<string, unknown>;
}

/** Podkladka (tayl manbai) — xarita ichidagi tanlagichda ko'rsatiladi. */
export interface MapBasemap {
  code: string;
  name: string;
  url_template: string;
  attribution?: string;
}

export interface MapShape {
  id: string;
  /** Poligon halqalari yoki chiziq segmentlari: [lon, lat] juftliklari. */
  rings: Array<Array<[number, number]>>;
  /**
   * Ko'pburchaklarning asl tuzilishi (har biri: tashqi halqa + teshiklar). Berilsa
   * teshikli yerlar to'g'ri chiziladi; berilmasa har bir halqa alohida ko'pburchak.
   */
  polygons?: Array<Array<Array<[number, number]>>>;
  /**
   * Chiziqli obyekt (daryo, yo'l, temir yo'l): kontur yopilmaydi va ichi
   * bo'yalmaydi. Berilmasa shakl poligon deb qaraladi.
   */
  line?: boolean;
  color?: string;
  /**
   * Qatlam stili (superadmin «Ikonka va uslublar» dizayneri, `MapLayer.style`):
   * to'ldirish shaffofligi, chiziq qalinligi va punktir naqshi. Berilmasa —
   * standart qiymatlar (0,25 / 1 px / uzluksiz).
   */
  fillOpacity?: number;
  strokeWidth?: number;
  strokeColor?: string;
  dash?: number[];
  title: string;
  subtitle?: string;
  /** Popup'da "Batafsil" bo'limida ko'rsatiladigan xom atributlar (metadata). */
  properties?: Record<string, unknown>;
}

/** Xaritani tashqaridan ko'rsatilgan joyga olib borish (qidiruv, «Mening joylashuvim»). */
export interface MapFocus {
  lat: number;
  lon: number;
  zoom?: number;
  /** Berilsa, xarita shu chegaraga (g, j, sh, shm) to'liq sig'adigan qilib o'tadi. */
  bounds?: [number, number, number, number];
  /** Har yangi so'rovda o'zgaradi — bir xil nuqtaga qayta o'tish ham ishlaydi. */
  key: string | number;
}

/**
 * Server vektor plitkalaridan chiziladigan qatlam (`GET /gis/tiles/{id}/{z}/{x}/{y}.pbf`).
 * Million obyektli qatlam ham shu bilan qotmasdan ko'rinadi: brauzer faqat ekrandagi
 * plitkalarni oladi. Ro'yxatda pastdagi qatlam birinchi chiziladi (yuqorisi ustida turadi).
 */
export interface VectorLayer {
  /** Xarita ichidagi noyob kalit (odatda yo'nalish kodi). */
  id: string;
  /** `MapLayer.id` — plitka manzili shundan. */
  layerId: number;
  /** `MapLayer.tiles_version` — qatlam o'zgarganda kesh o'zi yangilanadi. */
  version: number;
  geometry: "point" | "line" | "polygon";
  color: string;
  /** Qatlam nomi — bosilganda popup'da ko'rsatiladi. */
  label: string;
  /** Plitkalar shu masshtabdan boshlab so'raladi (qatlam zichligidan avtomatik). */
  minZoom?: number;
  maxZoom?: number;
  /**
   * Zich qatlam: shu masshtabgacha aniq obyektlar o'rniga (va ular ostida) umumlashtirilgan
   * qamrov poligoni ko'rsatiladi — «ma'lumot qayerda bor» uzoqdan ham bilinadi (`> 0` bo'lsa).
   */
  densityUntil?: number;
  fillOpacity?: number;
  strokeWidth?: number;
  strokeColor?: string;
  dash?: number[];
  /** Obyekt sarlavhasi uchun atributlar (birinchi to'lgani ishlatiladi). */
  titleKeys?: string[];
}

/** Xaritadagi alohida belgi (murojaat nuqtasi, foydalanuvchi joylashuvi). */
export interface MapPin {
  lat: number;
  lon: number;
  label: string;
  tone?: "brand" | "info" | "danger";
}

/** Ko'rinib turgan hudud — `onViewportChange` orqali tashqariga uzatiladi. */
export interface MapViewport {
  zoom: number;
  bounds: { west: number; south: number; east: number; north: number };
}

/** Geografik chegara (masalan qatlam ma'lumoti joylashgan hudud). */
export type MapBounds = MapViewport["bounds"];

/**
 * Xarita ustidagi panellar egallagan chetlar (piksel) — «Ma'lumotga
 * yaqinlashish»da ma'lumot ular ostida qolib ketmasligi uchun.
 */
export interface MapInsets {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}


/* ------------------------------------------------------- GeoJSON yordamchi */

interface GeoJsonLike {
  features: Array<{
    id?: string | number;
    geometry: { type: string; coordinates: unknown } | null;
    properties: Record<string, unknown>;
  }>;
}

/** GeoJSON to'plamidan nuqtalarni ajratadi. */
export function geoJsonPoints(
  collection: GeoJsonLike | null,
  color: string,
  titleKey: string,
  subtitleKey?: string,
): MapPoint[] {
  if (!collection?.features) return [];
  const points: MapPoint[] = [];
  for (const feature of collection.features) {
    if (feature.geometry?.type !== "Point") continue;
    const [lon, lat] = feature.geometry.coordinates as [number, number];
    points.push({
      id: String(feature.id ?? `${lon}-${lat}`),
      lat,
      lon,
      color: (feature.properties?.color as string) || color,
      title: String(feature.properties?.[titleKey] ?? "—"),
      subtitle: subtitleKey ? String(feature.properties?.[subtitleKey] ?? "") : undefined,
      properties: feature.properties,
    });
  }
  return points;
}


/**
 * GeoJSON to'plamidan chiziladigan shakllarni ajratadi.
 *
 * Poligonlar yopiq kontur bo'lib ichi bo'yaladi, chiziqli obyektlar
 * (`LineString` / `MultiLineString` — daryo, yo'l, temir yo'l) esa ochiq
 * qoladi: ular `line: true` belgisi bilan qaytariladi.
 */
export function geoJsonShapes(
  collection: GeoJsonLike | null,
  color: string,
  /** Bitta atribut nomi yoki ro'yxat — birinchi to'lgani sarlavha bo'ladi. */
  titleKey: string | string[],
): MapShape[] {
  const keys = Array.isArray(titleKey) ? titleKey : [titleKey];
  if (!collection?.features) return [];
  const shapes: MapShape[] = [];
  for (const feature of collection.features) {
    const geometry = feature.geometry;
    if (!geometry) continue;
    let rings: Array<Array<[number, number]>> = [];
    let polygons: Array<Array<Array<[number, number]>>> | undefined;
    let line = false;
    if (geometry.type === "Polygon") {
      rings = geometry.coordinates as Array<Array<[number, number]>>;
      polygons = [rings];
    } else if (geometry.type === "MultiPolygon") {
      polygons = geometry.coordinates as Array<Array<Array<[number, number]>>>;
      rings = polygons.flat();
    } else if (geometry.type === "LineString") {
      rings = [geometry.coordinates as Array<[number, number]>];
      line = true;
    } else if (geometry.type === "MultiLineString") {
      rings = geometry.coordinates as Array<Array<[number, number]>>;
      line = true;
    } else {
      continue;
    }
    shapes.push({
      id: String(feature.id ?? shapes.length),
      rings,
      polygons,
      line,
      color: (feature.properties?.color as string) || color,
      title: String(keys.map((key) => feature.properties?.[key]).find(Boolean) ?? "—"),
      properties: feature.properties,
    });
  }
  return shapes;
}

/** Server chegarasi `[minLon, minLat, maxLon, maxLat]` (`MapLayerRow.extent`) → `MapBounds`. */
export function extentBounds(extent: [number, number, number, number]): MapBounds {
  const [west, south, east, north] = extent;
  return { west, south, east, north };
}

/** Bir nechta chegarani qamrab oluvchi umumiy chegara; hech biri bo'lmasa — `null`. */
export function unionBounds(list: Array<MapBounds | null | undefined>): MapBounds | null {
  let result: MapBounds | null = null;
  for (const bounds of list) {
    if (!bounds) continue;
    result = result
      ? {
          west: Math.min(result.west, bounds.west),
          south: Math.min(result.south, bounds.south),
          east: Math.max(result.east, bounds.east),
          north: Math.max(result.north, bounds.north),
        }
      : { ...bounds };
  }
  return result;
}

/** Yuklangan nuqta va shakllar egallagan hudud; ma'lumot bo'lmasa — `null`. */
export function boundsOfData(points: MapPoint[], shapes: MapShape[]): MapBounds | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  const add = (lon: number, lat: number) => {
    if (lon < west) west = lon;
    if (lon > east) east = lon;
    if (lat < south) south = lat;
    if (lat > north) north = lat;
  };
  for (const point of points) add(point.lon, point.lat);
  for (const shape of shapes) {
    for (const ring of shape.rings) {
      for (const [lon, lat] of ring) add(lon, lat);
    }
  }
  return west <= east && south <= north ? { west, south, east, north } : null;
}
